import React, { useState, useEffect } from "react";
import { useAuth } from "./authContext";
import { getData } from "./firestoreService";
import { 
  createPatientShare, 
  revokePatientShare, 
  updateShareAccessLevel,
  getSharesForPatient, 
  getUsersInCompany 
} from "./sharingService";
import ModalPortal from "./ModalPortal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { shareRecipientFilterOptions } from "./shareRecipientFilter";
import { X } from 'lucide-react';
import { logAuditEventAsync, AuditActions } from "./auditLogger";

// Verbatim consent attestation string — must match UI and audit log exactly.
// See docs/features/document-system/patient-sharing.md § Consent Attestation Checkbox.
const CONSENT_ATTESTATION_TEXT = "I verify that the Patient or their Authorized Representative have already signed a Release to share health information with the recipient, and that this Release is on record.";

const SharePatient = ({ isOpen, setIsOpen, patientId, patientName }) => {
  const [companyUsers, setCompanyUsers] = useState([]);
  const [currentShares, setCurrentShares] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [accessLevel, setAccessLevel] = useState("view_add_private");
  const [consentChecked, setConsentChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  // Fetch company users and current shares when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !userId || !patientId) return;

      setIsLoading(true);
      setError("");

      try {
        // Get current user's company
        const userDoc = await getData(`/users/${userId}`);
        if (!userDoc?.companyId) {
          setError("Unable to load company information");
          setIsLoading(false);
          return;
        }

        // Fetch users in the same company (excluding current user)
        let users = [];
        try {
          users = await getUsersInCompany(userDoc.companyId, userId);
        } catch (userErr) {
          console.error("Error fetching company users:", userErr);
          // Continue - we can still show existing shares
        }
        setCompanyUsers(users);

        // Fetch current shares for this patient
        let shares = [];
        try {
          shares = await getSharesForPatient(patientId, userId);
        } catch (shareErr) {
          console.error("Error fetching shares:", shareErr);
          // Continue - show empty shares list
        }
        
        // Names come from the same company directory as the recipient picker.
        // A Standard owner cannot read another user's private profile directly.
        setCurrentShares(shares);
        
        // Only show error if we couldn't load users AND this is needed for sharing
        if (users.length === 0 && shares.length === 0) {
          setError("No other users found in your company to share with");
        }
      } catch (err) {
        console.error("Error loading share data:", err);
        setError(`Failed to load sharing data: ${err.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isOpen, userId, patientId]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedUser(null);
      setAccessLevel("view_add_private");
      setConsentChecked(false);
      setError("");
    }
  }, [isOpen]);

  const handleShare = async () => {
    if (!selectedUser || isSubmitting) return;

    // Check if user already has an active share
    const existingShare = currentShares.find(
      share => share.sharedWithUserId === selectedUser.id
    );
    if (existingShare) {
      setError("This patient is already shared with this user");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const userDoc = await getData(`/users/${userId}`);
      
      await createPatientShare({
        patientId,
        ownerId: userId, // Current user is the patient owner
        sharedWithUserId: selectedUser.id,
        accessLevel,
        sharedBy: userId,
        companyId: userDoc.companyId,
        // Access Expiry deferred to future Patient Portal per 42 CFR Part 2 /
        // Information Blocking (Monday #11875687176). Sharing clinician has no
        // legal authority to set an expiry; only the patient/representative does.
        expiresAt: null,
        // Pass-through for recipient bell-icon notification (Monday #11875687071)
        patientName,
        consentAttested: true,
      });

      // Consent audit and notification were committed by the server.

      // Refresh shares list
      const shares = await getSharesForPatient(patientId, userId);
      setCurrentShares(shares);

      // Reset form
      setSelectedUser(null);
      setAccessLevel("view");

      // Close modal on success — share is created, user shouldn't need to dismiss manually
      // (Userback #7585709 — Ben reported the modal stayed open after Proceed to Share).
      setIsOpen(false);
    } catch (err) {
      console.error("Error creating share:", err);
      setError("Failed to share patient");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAccessLevel = async (shareId, newAccessLevel, previousLevel) => {
    if (isSubmitting || newAccessLevel === previousLevel) return;

    setIsSubmitting(true);
    setError("");

    try {
      await updateShareAccessLevel(shareId, newAccessLevel, patientId, currentShares.find(s => s.id === shareId)?.sharedWithUserId);

      // Audit log
      logAuditEventAsync(AuditActions.PATIENT_SHARE_UPDATE, {
        resourceType: 'patient',
        resourceId: patientId,
        patientId: patientId,
        metadata: {
          shareId,
          previousAccessLevel: previousLevel,
          newAccessLevel
        }
      });

      // Update local state
      setCurrentShares(prev => prev.map(share =>
        share.id === shareId ? { ...share, accessLevel: newAccessLevel } : share
      ));
    } catch (err) {
      console.error("Error updating share access level:", err);
      setError("Failed to update access level");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevoke = async (shareId) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError("");

    try {
      const share = currentShares.find(s => s.id === shareId);
      await revokePatientShare(shareId, patientId, share?.sharedWithUserId);

      // Audit log
      logAuditEventAsync(AuditActions.PATIENT_SHARE_REVOKE, {
        resourceType: 'patient',
        resourceId: patientId,
        patientId: patientId,
        metadata: { shareId }
      });

      // Remove from local state
      setCurrentShares(prev => prev.filter(share => share.id !== shareId));
    } catch (err) {
      console.error("Error revoking share:", err);
      setError("Failed to revoke share");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAccessLevelLabel = (level) => {
    switch (level) {
      case "view": return "View only";
      case "view_add": return "View + Add notes";
      case "view_add_private": return "View + Add (Private)";
      case "full": return "Full access";
      default: return level;
    }
  };

  // Filter out users who already have an active share
  const availableUsers = companyUsers.filter(
    user => !currentShares.some(share => share.sharedWithUserId === user.id)
  );

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="modal" onClick={() => setIsOpen(false)}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h3 className="modal-title">Share {patientName}</h3>
            <hr className="modal-header-rule" />

            {isLoading ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-medium-gray)' }}>
                Loading...
              </div>
            ) : (
              <>
                {/* New Share Form */}
                <div className="modal-form">
                  <div className="modal-section">
                    <p className="modal-section-label">Share with:</p>
                    <Autocomplete
                      options={availableUsers}
                      filterOptions={shareRecipientFilterOptions}
                      getOptionLabel={(option) => option.displayName || option.email || "Unknown"}
                      value={selectedUser}
                      onChange={(event, newValue) => setSelectedUser(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search users in your organization..."
                          className="modal-input"
                          variant="outlined"
                          fullWidth
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <div>
                            <div style={{ fontWeight: 500 }}>{option.displayName || "No name"}</div>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-medium-gray)' }}>
                              {option.email}
                            </div>
                          </div>
                        </li>
                      )}
                      noOptionsText="No users available"
                      disabled={availableUsers.length === 0}
                    />
                  </div>

                  <div style={{ 
                    padding: '12px', 
                    backgroundColor: 'var(--color-light-background)', 
                    borderRadius: '6px',
                    border: '1px solid var(--color-light-border)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-medium-gray)'
                  }}>
                    Can view patient history and add documents. Documents they create are private — you can view but not edit them.
                  </div>

                  {error && (
                    <div className="error-message" style={{ marginTop: '8px' }}>
                      {error}
                    </div>
                  )}
                </div>

                {/* Current Shares List */}
                {currentShares.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <p className="modal-section-label">Currently shared with:</p>
                    <div style={{ 
                      border: '1px solid var(--color-light-border)', 
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      {currentShares.map((share, index) => {
                        const recipient = companyUsers.find(user => user.id === share.sharedWithUserId);
                        const recipientName = recipient?.displayName || recipient?.email || "Unknown User";
                        return (
                        <div
                          key={share.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            borderBottom: index < currentShares.length - 1 ? '1px solid var(--color-light-border)' : 'none',
                            backgroundColor: 'var(--color-light-background)'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500 }}>{recipientName}</div>
                            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-medium-gray)', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                              <span>{getAccessLevelLabel(share.accessLevel)}</span>
                            </div>
                          </div>
                          {/* Revocation button removed 2026-04-28 per Ben (legal blocker, Userback #7585729).
                              Revoke flow deferred to a future Patient Portal. `handleRevoke` and the
                              `revokePatientShare` service remain in place for that future surface. */}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Consent attestation — required before every share.
                    See docs/features/document-system/patient-sharing.md. */}
                <div style={{ marginTop: '20px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer',
                    padding: '12px',
                    border: '1px solid var(--color-light-border)',
                    borderRadius: '6px',
                    backgroundColor: consentChecked ? 'var(--color-light-background)' : 'transparent',
                    transition: 'background-color 0.15s',
                  }}>
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(e) => setConsentChecked(e.target.checked)}
                      style={{ marginTop: '3px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', lineHeight: 1.45 }}>
                      {CONSENT_ATTESTATION_TEXT}
                    </span>
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="modal-button-container" style={{ marginTop: '24px' }}>
                  <button
                    className="button-cancel-secondary"
                    onClick={() => setIsOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    className="button-confirm"
                    onClick={handleShare}
                    disabled={!selectedUser || !consentChecked || isSubmitting}
                  >
                    {isSubmitting ? "Sharing..." : "Proceed to Share"}
                  </button>
                </div>
              </>
            )}
          </div>
        </LocalizationProvider>
      </div>
    </ModalPortal>
  );
};

export default SharePatient;
